export const ELIGIBLE_PO_QTY_STATUS_KEY = "__eligiblePoQtyStatus";
export const ELIGIBLE_PO_QTY_DIAGNOSTIC_KEY = "__eligiblePoQtyDiagnostic";

const normalize = (value) => (value === undefined || value === null ? "" : String(value).trim());

export function calculateAllocationStats({
  dataset,
  pendingEdits,
  getItemsForRow,
  parseAllocValue,
  custKey,
  poNumberKey,
  poSharpKey,
  eligibleKey,
  rowIdKey,
}) {
  const rowSums = new Map();
  const rowToPo = new Map();
  const poRowCounts = new Map();
  const poTotals = new Map();
  const poTargets = new Map();
  const poDiagnostics = new Map();
  const unavailablePoKeys = new Set();
  const editedRowKeys = new Set(Object.keys(pendingEdits || {}));
  const editedPoKeys = new Set();

  (dataset?.data || []).forEach((row) => {
    if (!row || typeof row !== "object") return;
    const rowKey = row[rowIdKey];
    if (!rowKey) return;
    const custCode = normalize(row[custKey]).toUpperCase();
    const poValue = normalize(row[poNumberKey] || row[poSharpKey]);
    if (!custCode || !poValue) return;
    const poKey = `${custCode}|${poValue}`;
    rowToPo.set(rowKey, poKey);
    if (editedRowKeys.has(rowKey)) editedPoKeys.add(poKey);
    poRowCounts.set(poKey, (poRowCounts.get(poKey) || 0) + 1);

    const bySku = new Map();
    (getItemsForRow(row) || []).forEach((item) => {
      const sku = normalize(item?.sku);
      if (sku) bySku.set(sku, parseAllocValue(item?.allocQty));
    });
    Object.entries(pendingEdits?.[rowKey] || {}).forEach(([sku, value]) => {
      const normalizedSku = normalize(sku);
      if (normalizedSku) bySku.set(normalizedSku, parseAllocValue(value));
    });
    let rowSum = 0;
    bySku.forEach((value) => {
      rowSum += parseAllocValue(value);
    });
    rowSums.set(rowKey, rowSum);
    poTotals.set(poKey, (poTotals.get(poKey) || 0) + rowSum);

    const status = normalize(row[ELIGIBLE_PO_QTY_STATUS_KEY]).toUpperCase();
    const rawTarget = eligibleKey ? row[eligibleKey] : null;
    const targetMissing =
      !eligibleKey ||
      status === "UNAVAILABLE" ||
      rawTarget === null ||
      rawTarget === undefined ||
      rawTarget === "" ||
      Number.isNaN(Number(rawTarget));
    if (targetMissing) {
      unavailablePoKeys.add(poKey);
      poDiagnostics.set(
        poKey,
        normalize(row[ELIGIBLE_PO_QTY_DIAGNOSTIC_KEY]) ||
          "Eligible PO QTY is unavailable for this Cust Code and PO. Allocation changes cannot be saved."
      );
      return;
    }
    const target = parseAllocValue(rawTarget);
    if (poTargets.has(poKey) && poTargets.get(poKey) !== target) {
      unavailablePoKeys.add(poKey);
      poDiagnostics.set(
        poKey,
        "Eligible PO QTY is inconsistent across active Routing rows for this Cust Code and PO. Allocation changes cannot be saved."
      );
      return;
    }
    poTargets.set(poKey, target);
  });

  const rowMismatch = new Set();
  const poMismatch = new Set();
  rowToPo.forEach((poKey, rowKey) => {
    if (unavailablePoKeys.has(poKey)) return;
    const count = poRowCounts.get(poKey) || 0;
    if (count <= 1 && poTargets.has(poKey) && rowSums.get(rowKey) !== poTargets.get(poKey)) {
      rowMismatch.add(rowKey);
    }
  });
  poTotals.forEach((total, poKey) => {
    if (unavailablePoKeys.has(poKey)) return;
    const count = poRowCounts.get(poKey) || 0;
    if (count > 1 && poTargets.has(poKey) && total !== poTargets.get(poKey)) {
      poMismatch.add(poKey);
    }
  });

  const blockingRowKeys = new Set([...rowMismatch].filter((rowKey) => editedRowKeys.has(rowKey)));
  const blockingPoKeys = new Set([...poMismatch].filter((poKey) => editedPoKeys.has(poKey)));
  const blockingUnavailablePoKeys = new Set(
    [...unavailablePoKeys].filter((poKey) => editedPoKeys.has(poKey))
  );

  return {
    rowSums,
    rowMismatch,
    poMismatch,
    rowToPo,
    editedPoKeys,
    unavailablePoKeys,
    poDiagnostics,
    hasBlockingMismatch:
      blockingRowKeys.size > 0 || blockingPoKeys.size > 0 || blockingUnavailablePoKeys.size > 0,
  };
}
